import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DebounceInput } from 'react-debounce-input'
import Toggle from 'react-toggle'
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMobileAlt } from '@fortawesome/free-solid-svg-icons'
import {
  iconSearchMainView,
} from '../../icons'
import './SearchBar.css'
import { Filter } from '../../modules/Mamorio'

type ListType = 'mamorio' | 'device' | 'gps_tracker' | 'qr_tag'

type Props = {
  rentalMode?: boolean
  loadList: (useDeviceApi: boolean) => void
  updateGroupFilter: (newFilter: Pick<Filter, 'isRent' | 'name' | 'memo'>) => void
  showDeviceSwitch?: boolean
  showSelector?: boolean
  currentListType?: ListType
  onListTypeChange?: (listType: ListType) => void
  memoEnabled?: boolean
  listTypes?: ListType[]
  qrTagKeyword?: string
  onQrTagSearch?: (keyword: string) => void
  qrTagLatestEventType?: string
  onQrTagLatestEventTypeChange?: (eventType: string) => void
  qrTagEventTypeOptions?: string[]
  showQrTagLatestEventTypeSelector?: boolean
}

const BOUNCE_TIME = 500
const LIST_TYPE_LABELS: Record<ListType, string> = {
  mamorio: 'MAMORIO',
  device: '端末',
  gps_tracker: 'GPSトラッカー',
  qr_tag: 'QRタグ',
}

/**
 * MAMORIO または端末の表示条件を入力・変更する UI
 * @param rentalMode
 * @param loadList
 * @param updateGroupFilter
 * @param showDeviceSwitch
 */
const SearchBar = ({
  rentalMode = false,
  loadList,
  updateGroupFilter,
  showDeviceSwitch = true,
  showSelector = false,
  currentListType = 'mamorio',
  onListTypeChange,
  memoEnabled = false,
  listTypes = ['mamorio', 'device', 'gps_tracker'],
  qrTagKeyword = '',
  onQrTagSearch,
  qrTagLatestEventType = '',
  onQrTagLatestEventTypeChange,
  qrTagEventTypeOptions = [],
  showQrTagLatestEventTypeSelector = true,
}: Props) => {
  const [isEnableDeviceListMode, setIsEnableDeviceListMode] = useState(false)
  const [isRentalDropdownOpen, setIsRentalDropdownOpen] = useState(false)
  const [isListTypeDropdownOpen, setIsListTypeDropdownOpen] = useState(false)
  const [isRent, setIsRent] = useState<boolean | null>(null)
  const [qrKeywordInput, setQrKeywordInput] = useState(qrTagKeyword)

  const loadDeviceList = useCallback(() => {
    loadList(isEnableDeviceListMode)
  }, [loadList, isEnableDeviceListMode])

  const updateFilterIsRent = (value: boolean | null) => {
    updateGroupFilter({ isRent: value })
    setIsRent(value)
  }

  useEffect(() => {
    if (!showSelector) {
      loadDeviceList()
    }
  }, [loadDeviceList, isEnableDeviceListMode, showSelector])

  const availableListTypes = useMemo(
    () => listTypes.filter((listType) => showDeviceSwitch || listType !== 'device'),
    [listTypes, showDeviceSwitch],
  )

  const handleQrTagSearch = useCallback((keyword: string) => {
    onQrTagSearch?.(keyword)
  }, [onQrTagSearch])

  useEffect(() => {
    setQrKeywordInput(qrTagKeyword)
  }, [qrTagKeyword])

  const showMamorioFilters = currentListType !== 'qr_tag'

  return (
    <div className="search-bar" data-testid="search-bar">
      <div className="filter-menu">
        <div className="filter-title top">
          <img src={iconSearchMainView} className="search-icon" alt="search" />
          <div className="label">
            物品を検索する
          </div>
        </div>
        {showMamorioFilters ? (
          <div className="filter-input">
            <div className="filter-title">
              MAMORIOの名前
            </div>
            <DebounceInput
              debounceTimeout={BOUNCE_TIME}
              disabled={isEnableDeviceListMode}
              onChange={
                (event: React.ChangeEvent<HTMLInputElement>) => updateGroupFilter({
                  name: event.target.value,
                })
              }
              type="text"
              className="form-control"
              placeholder="MAMORIOの名前"
              data-testid="search-bar-mamorio-name-input"
            />
          </div>
        ) : (
          <div className="filter-input">
            <div className="filter-title">
              キーワード
            </div>
            <DebounceInput
              debounceTimeout={BOUNCE_TIME}
              value={qrKeywordInput}
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = target
                setQrKeywordInput(value)
                handleQrTagSearch(value)
              }}
              type="text"
              className="form-control"
              placeholder="キーワードで検索"
              data-testid="search-bar-qr-keyword-input"
            />
          </div>
        )}
        {memoEnabled && showMamorioFilters && (
          <div className="filter-input">
            <div className="filter-title" style={{ width: '30px' }}>
              メモ
            </div>
            <DebounceInput
              debounceTimeout={BOUNCE_TIME}
              disabled={isEnableDeviceListMode}
              onChange={
                (event: React.ChangeEvent<HTMLInputElement>) => updateGroupFilter({
                  memo: event.target.value,
                })
              }
              type="text"
              className="form-control"
              placeholder="メモの内容"
              data-testid="search-bar-mamorio-name-input"
            />
          </div>
        )}
        {!showMamorioFilters && showQrTagLatestEventTypeSelector && (
          <div className="filter-status">
            <div className="filter-title" style={{ width: 'auto' }}>
              最新イベント種別
            </div>
            <select
              className="form-control"
              value={qrTagLatestEventType}
              onChange={(event) => onQrTagLatestEventTypeChange?.(event.target.value)}
              data-testid="search-bar-qr-latest-event-type-select"
            >
              <option value="">すべて</option>
              {qrTagEventTypeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        )}
        {rentalMode && showMamorioFilters && (
          <div className="filter-status">
            <div className="filter-title">
              持出状況
            </div>
            <Dropdown
              isOpen={isRentalDropdownOpen}
              toggle={() => setIsRentalDropdownOpen(!isRentalDropdownOpen)}
              data-testid="search-bar-lend-status-dropdown"
            >
              <DropdownToggle
                caret
                color="white"
                disabled={isEnableDeviceListMode}
                data-testid="search-bar-lend-status-dropdown-toggle"
              >
                {isRent === null && 'すべて'}
                {isRent === true && '持出中'}
                {isRent === false && '返却済'}
              </DropdownToggle>
              <DropdownMenu data-testid="search-bar-lend-status-dropdown-menu">
                <DropdownItem onClick={() => updateFilterIsRent(null)}>
                  すべて
                </DropdownItem>
                <DropdownItem onClick={() => updateFilterIsRent(true)}>
                  持出中
                </DropdownItem>
                <DropdownItem onClick={() => updateFilterIsRent(false)}>
                  返却済
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        {(() => {
          if (showSelector) {
            return (
              <div className="filter-status">
                <div className="filter-title" style={{ width: 'auto' }}>
                  リストを切り替える
                </div>
                <Dropdown
                  isOpen={isListTypeDropdownOpen}
                  toggle={() => setIsListTypeDropdownOpen(!isListTypeDropdownOpen)}
                  data-testid="search-bar-list-type-dropdown"
                >
                  <DropdownToggle
                    caret
                    color="white"
                    data-testid="search-bar-list-type-dropdown-toggle"
                  >
                    {LIST_TYPE_LABELS[currentListType]}
                  </DropdownToggle>
                  <DropdownMenu data-testid="search-bar-list-type-dropdown-menu">
                    {availableListTypes.map((type) => (
                      <DropdownItem
                        key={type}
                        onClick={() => {
                          onListTypeChange?.(type)
                          setIsListTypeDropdownOpen(false)
                        }}
                      >
                        {LIST_TYPE_LABELS[type]}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>
            )
          }
          if (showDeviceSwitch && showMamorioFilters) {
            return (
              <div className="filter-toggle">
                <div className="filter-title">
                  端末の一覧を見る
                </div>
                <Toggle
                  defaultChecked={isEnableDeviceListMode}
                  icons={{
                    checked: <FontAwesomeIcon icon={faMobileAlt} />,
                    unchecked: null,
                  }}
                  onChange={
                    (e: React.ChangeEvent<HTMLInputElement>) => setIsEnableDeviceListMode(e.target.checked)
                  }
                  data-testid="search-bar-display-mode-toggle"
                />
              </div>
            )
          }
          return null
        })()}
      </div>
    </div>
  )
}

export default SearchBar
